import * as React from 'react';
import { clsx } from 'clsx';
import { AlertCircle, AlertTriangle, Info, X } from 'lucide-react';

export type BannerVariant = 'warning' | 'destructive' | 'information' | 'neutral';

export interface BannerProps {
  /**
   * Banner variant - determines color scheme
   */
  variant?: BannerVariant;
  /**
   * Optional title text
   */
  title?: string;
  /**
   * Main message/description text
   */
  message: string;
  /**
   * Optional icon (pass false to hide default icon, or pass custom ReactNode)
   */
  icon?: React.ReactNode | boolean;
  /**
   * Optional action button label
   */
  actionLabel?: string;
  /**
   * Action button click handler
   */
  onAction?: () => void;
  /**
   * Optional close button
   */
  onClose?: () => void;
  /**
   * Custom className
   */
  className?: string;
  /**
   * Custom style
   */
  style?: React.CSSProperties;
  /**
   * Test ID for testing
   */
  'data-testid'?: string;
}

const containerStyles: React.CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  padding: '16px',
  borderRadius: '8px',
  border: '1px solid',
  width: '100%',
  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
};

const iconContainerStyles: React.CSSProperties = {
  flexShrink: 0,
  width: '24px',
  height: '24px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const contentContainerStyles: React.CSSProperties = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  minWidth: 0,
};

const titleStyles: React.CSSProperties = {
  fontSize: '18px',
  fontWeight: 600,
  lineHeight: '1.25',
  marginBottom: '8px',
};

const messageStyles: React.CSSProperties = {
  fontSize: '13px',
  fontWeight: 400,
  lineHeight: '1.5',
  margin: 0,
};

const actionContainerStyles: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-end',
  alignItems: 'flex-end',
  alignSelf: 'stretch',
  flexShrink: 0,
};

const actionButtonStyles: React.CSSProperties = {
  background: 'none',
  border: 'none',
  padding: 0,
  fontSize: '13px',
  fontWeight: 600,
  lineHeight: '1.5',
  textDecoration: 'underline',
  textUnderlinePosition: 'from-font',
  cursor: 'pointer',
  fontFamily: 'inherit',
};

const closeButtonStyles: React.CSSProperties = {
  background: 'none',
  border: 'none',
  padding: '4px',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  marginLeft: '8px',
};

const variantConfig = {
  warning: {
    backgroundColor: '#fffaf5',
    borderColor: '#e4720d',
    textColor: '#611f00',
    actionColor: '#a74102',
    icon: AlertTriangle,
  },
  destructive: {
    backgroundColor: '#fff5f5',
    borderColor: '#c93232',
    textColor: '#610202',
    actionColor: '#920a0a',
    icon: AlertCircle,
  },
  information: {
    backgroundColor: '#f5fbff',
    borderColor: '#2c8bca',
    textColor: '#053a61',
    actionColor: '#024f83',
    icon: Info,
  },
  neutral: {
    backgroundColor: 'transparent',
    borderColor: '#b3b3b3',
    textColor: '#2f2f2f',
    actionColor: '#2f2f2f',
    icon: Info,
  },
};

/**
 * Banner component - Arbor Design System
 *
 * Informational banners with 4 variants (warning, destructive, information, neutral)
 * and optional title, icon, and action button.
 *
 * IMPORTANT: Banners are always displayed inline with page content (not overlays).
 * They flow with the document layout and push other content down.
 *
 * For overlay notifications in the top-right corner, use the Toast component instead.
 */
export const Banner = React.forwardRef<HTMLDivElement, BannerProps>(
  (
    {
      variant = 'information',
      title,
      message,
      icon = true,
      actionLabel,
      onAction,
      onClose,
      className,
      style,
      'data-testid': dataTestId,
    },
    ref
  ) => {
    const config = variantConfig[variant];
    const DefaultIcon = config.icon;

    // Determine what icon to show
    const showIcon = icon !== false;
    const iconElement =
      icon === true || icon === undefined ? (
        <DefaultIcon size={20} strokeWidth={2} />
      ) : (
        icon
      );

    const containerStylesCombined: React.CSSProperties = {
      ...containerStyles,
      backgroundColor: config.backgroundColor,
      borderColor: config.borderColor,
      color: config.textColor,
      gap: showIcon ? '16px' : '0px',
      ...style,
    };

    const titleStylesCombined: React.CSSProperties = {
      ...titleStyles,
      color: config.textColor,
    };

    const messageStylesCombined: React.CSSProperties = {
      ...messageStyles,
      color: config.textColor,
    };

    const actionButtonStylesCombined: React.CSSProperties = {
      ...actionButtonStyles,
      color: config.actionColor,
    };

    const closeButtonStylesCombined: React.CSSProperties = {
      ...closeButtonStyles,
      color: config.textColor,
    };

    return (
      <div
        ref={ref}
        className={clsx('arbor-banner', `arbor-banner--${variant}`, className)}
        style={containerStylesCombined}
        data-testid={dataTestId}
        role="alert"
      >
        {showIcon && (
          <div style={{ ...iconContainerStyles, color: config.textColor }}>
            {iconElement}
          </div>
        )}

        <div style={contentContainerStyles}>
          {title && <div style={titleStylesCombined}>{title}</div>}
          <p style={messageStylesCombined}>{message}</p>
        </div>

        {actionLabel && onAction && (
          <div style={actionContainerStyles}>
            <button
              type="button"
              onClick={onAction}
              style={actionButtonStylesCombined}
            >
              {actionLabel}
            </button>
          </div>
        )}

        {onClose && (
          <button
            type="button"
            onClick={onClose}
            style={closeButtonStylesCombined}
            aria-label="Close banner"
          >
            <X size={20} />
          </button>
        )}
      </div>
    );
  }
);

Banner.displayName = 'Banner';
